import { makeStyles } from '@material-ui/styles'
import { flavorsSelector } from 'app/plugins/openstack/components/flavors/selectors'
import { humanReadableSize } from 'app/plugins/openstack/helpers'
import { ArrayElement } from 'core/actions/Action'
import GridSearchFilter from 'core/elements/grid/GridSearchFilter'
import QuantitySelector from 'pf9-ui-components/built/components/QuantitySelector'
import ToggleSwitchField from 'pf9-ui-components/built/components/validatedForm/ToggleSwitchField'
import Grid, { GridViewColumn } from 'pf9-ui-components/built/elements/grid/Grid'
import Text from 'pf9-ui-components/built/elements/Text'
import Theme from 'pf9-ui-components/built/theme-manager/themes/model'
import { path } from 'ramda'
import React, { useEffect, useMemo, useState } from 'react'
import GPUFlavorTag from './GPUTag'

const VmExpandedRow = ({ quantity = 1, onChange }) => {
  const classes = useVmExpandedRowStyles()
  return (
    <div className={classes.expandedRowContainer}>
      <Text variant="body2">Quantity</Text>
      <QuantitySelector id="quantity" onChange={onChange} value={quantity} min={1} />
    </div>
  )
}

const useVmExpandedRowStyles = makeStyles((theme: Theme) => ({
  expandedRowContainer: {
    display: 'grid',
    gap: theme.spacing(1),
    padding: theme.spacing(2),
    marginLeft: '42px',
  },
  vmFlavorsTable: {
    '& > div > div > div > div': {
      '&:first-child > div:last-child > div:last-child': {
        display: 'none',
      },
      '&:last-child': {
        gap: 0,
      },
    },
  },
  toggleContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: theme.spacing(0, 2, 2),
  },
}))

type SelectorModel = ArrayElement<ReturnType<typeof flavorsSelector>>

const columns: GridViewColumn<SelectorModel>[] = [
  {
    key: 'name',
    label: 'Virtual Machine Flavor',
    CellComponent: ({ item }) =>
      item?.extra_specs?.['pci_passthrough:alias'] ? (
        <GPUFlavorTag>
          <Text variant="body2">{item.name}</Text>
        </GPUFlavorTag>
      ) : (
        <Text variant="body2">{item.name}</Text>
      ),
  },
  { key: 'vcpus', label: 'vCPUs' },
  {
    key: 'ram',
    label: 'Memory (Gb)',
    render: (ram) => humanReadableSize(ram * 1024 * 1024, false),
  },
  {
    key: 'disk',
    label: 'Storage (Gb)',
    render: (disk) => humanReadableSize(disk * 1024 * 1024 * 1024, false),
  },
]

const searchTargets = ['name']

const globalFilters = [
  {
    key: 'search',
    equalityComparerFn: (item, value) => {
      return !!searchTargets.find((key) =>
        String(
          Object.prototype.hasOwnProperty.call(item, key)
            ? item[String(key)]
            : path(String(key).split('.'), item),
        )
          .toLocaleLowerCase()
          .includes(value.toLocaleLowerCase()),
      )
    },
    FilterComponent: GridSearchFilter,
  },
]

export default function VirtualMachineFlavorsTable({
  data,
  selectedVmFlavor,
  selectedVmQuantity,
  onChange,
}) {
  const classes = useVmExpandedRowStyles()
  const [selectedFlavors, setSelectedVms] = useState([])
  const [showOnlyGPUFlavors, setShowOnlyGPUFlavors] = useState(false)

  const filteredData = useMemo(() => {
    return showOnlyGPUFlavors
      ? data.filter((flavor) => flavor.extra_specs?.['pci_passthrough:alias'])
      : data
  }, [showOnlyGPUFlavors, data])

  const handleVmSelection = (selectedItems: SelectorModel[]) => {
    setSelectedVms(selectedItems)
    onChange({
      flavor: selectedItems[0]?.name,
      replicas: selectedVmQuantity || 1,
      gpuSupported: selectedItems[0]?.extra_specs?.gpu === 'true',
    })
  }

  const handleVmQuantityChange = (quantity: number) => {
    onChange({ replicas: quantity })
  }

  useEffect(() => {
    if (selectedFlavors[0]?.name === selectedVmFlavor) return
    setSelectedVms(data.filter((cluster) => cluster.name === selectedVmFlavor))
  }, [selectedVmFlavor, selectedFlavors[0]?.name, data])

  return (
    <div className={classes.vmFlavorsTable} data-testid="vm-flavors-table">
      <div className={classes.toggleContainer}>
        <ToggleSwitchField
          id="showGPUFlavorsOnly"
          data-testid="show-gpu-flavors-only-toggle"
          label="Show GPU flavors only"
          value={showOnlyGPUFlavors}
          onChange={() => setShowOnlyGPUFlavors(!showOnlyGPUFlavors)}
        />
      </div>
      <Grid<SelectorModel>
        uniqueIdentifier="id"
        columns={columns}
        data={filteredData}
        label="Virtual Machine Flavors"
        orderBy="vcpus"
        orderDirection="asc"
        emptyContent="No VM flavors available"
        disableRowSelection={false}
        multiSelection={false}
        selectedItems={selectedFlavors}
        onSelectChange={handleVmSelection}
        expandableRow={() => {
          return <VmExpandedRow quantity={selectedVmQuantity} onChange={handleVmQuantityChange} />
        }}
        expandedByDefault={(vm) => vm.name === selectedVmFlavor}
        expandRowsUponSelection
        globalFilters={globalFilters}
        disableColumnHiding
      />
    </div>
  )
}
